import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FooterDataDto,
  SortColumnDto,
  TableColumnConfigDto,
  TableDataDto,
  TableDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  AG_GRID_GROUP_AUTO_COLUMN_ID,
  Table as UiTable,
  Widget,
  WidgetProps,
  WidgetWithPaginator,
} from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import {
  ColumnResizedEvent,
  GROUP_AUTO_COLUMN_ID,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { useShallow } from 'zustand/react/shallow';

import {
  FetchDatatableDataQueryKeyParams,
  useTableDataQuery,
} from '../../api/portalGateway/datatableData';
import {
  FetchGroupedTableDataQueryKeyParams,
  useGroupedTableDataQuery,
} from '../../api/portalGateway/groupedTableData';
import { fetchPage, PageQueryKey } from '../../api/portalGateway/page';
import { useTenantInfoData } from '../../api/portalGateway/tenantInfo';
import {
  useCustomTableFilterQuery,
  useGlobalFilterLoaded,
  usePageFilterQuery,
  useTableFilterQuery,
} from '../../hooks/filter';
import { usePersistentPagination } from '../../hooks/usePersistentPagination';
import { usePersistentSorting } from '../../hooks/usePersistentSorting';
import { useStore } from '../../store';
import {
  getCustomPageFilterInstanceId,
  useCombinedPageFilterSelections,
} from '../../store/hooks/useCombinedPageFilterSelections';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { useIsDashboardDraftMode } from '../../store/states/dashboardEditMode';
import { useStakeAdjustmentStore } from '../../store/states/stakeAdjustment';
import { TableGroupExpansionsValue } from '../../store/states/tableGroupExpansions';
import {
  DEFAULT_GROUP_TABLE_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  PAGINATION_SIZES,
  PAGINATION_SIZES_DETAIL_PAGE,
  TRANSIENT_WORKFLOW_STATE_POLLING_INTERVAL,
} from '../../utils/constants';
import { extractErrorMessage } from '../../utils/errorMessage';
import { getTableFilterInstanceId } from '../../utils/filterInstanceId';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';
import { prependRoleId } from '../../utils/prependRoleId';
import { getRowIdsForTransientWorkflows } from '../../utils/rowDataHelper';
import type { DashboardFilterSelections } from '../customizableDashboard/dashboardTileFilters';
import { useDashboardContentEditActions } from '../customizableDashboard/useDashboardContentEditActions';
import { useDashboardTileFilters } from '../customizableDashboard/useDashboardTileFilters';
import { CustomTableFilters } from '../filter/customTableFilter/CustomTableFilters';
import { PageFilters } from '../filter/PageFilters';
import { TableFilters } from '../filter/TableFilters';
import { WidgetFilterContainer } from '../filter/WidgetFilterContainer';
import { WorkflowStateFilters } from '../filter/WorkflowStateFilters';
import { investmentComplianceLimitLinkColumnKey as firstInvestmentLimitTextColumnKey } from '../investmentLimit/investmentComplianceLimitLinkColumn';
import { StakeAdjustmentToggle } from '../page/StakeAdjustmentToggle';
import { useContentEditActions } from '../page/useContentEditActions';

import {
  isActiveSidebarRowInTableItems,
  resolveActiveSidebarRowHighlightId,
} from './sidebar/isActiveSidebarRowInTableItems';
import { useTableSidebarProperties } from './sidebar/useTableSidebarProperties';
import { configToColDef } from './configToColDef';
import { isDataRowVisible } from './isDataRowVisible';
import { CustomColumnWidths } from './useCustomization';
import { useFooter } from './useFooter';
import { useGroupColumnHeaderName } from './useGroupColumnHeaderName';
import { useInlineCellEditing } from './useInlineCellEditing';
import { usePageFilterColumnState } from './usePageFilterColumnState';
import {
  BaseUseTableWidgetActionProps,
  useTableWidgetActions,
} from './useTableWidgetActions';
import { useTransientWorkflowStatePolling } from './useTransientWorkflowStatePolling';

export type TableContentProps = {
  activeCustomFilterIds: string[];
  collapsible: boolean;
  customizedColumns: TableColumnConfigDto[];
  customPageFilterStoreId?: string | null;
  defaultPageSize?: number;
  enableScrollElementToTop?: boolean;
  entity: TableDto;
  groupColumnWidth?: number;
  groupOnlyVisualizationData: WidgetProps['chipGroup'];
  isColumnInitiallyHidden: boolean;
  isFullScreenMode?: boolean;
  isGroupedTable: boolean;
  onInvestmentLimitRowOpened?: (row: TableRowDto['data']) => void;
  onReadonlyWorkflowModalOpened?: (
    modalId: string,
    data: TableRowDto['data'][]
  ) => void;
  onColumnWidthChanged?: (item: CustomColumnWidths) => void;
  pageFilterInstanceId: string;
  pageId: string;
  /** When set with dashboardContentId, draft-mode layout actions replace widget chrome. */
  dashboardId?: string | null;
  dashboardContentId?: string;
  contentSourcePageId?: string;
  /** True while this tile's active custom-filter list is still loading. */
  isFilterCustomizationLoading?: boolean;
  savedPageFilterSelections?: DashboardFilterSelections;
  savedCustomTableFilterSelections?: DashboardFilterSelections;
  widgetFilterInstanceId: string;
} & BaseUseTableWidgetActionProps;

export function TableContent({
  activeCustomFilterIds,
  collapsible,
  customizedColumns,
  customPageFilterStoreId,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  enableScrollElementToTop,
  entity,
  groupColumnWidth,
  groupOnlyVisualizationData,
  isFullScreenMode = false,
  onColumnWidthChanged,
  onInvestmentLimitRowOpened,
  onModalOpened,
  onReadonlyWorkflowModalOpened,
  pageFilterInstanceId,
  pageId,
  dashboardId = null,
  dashboardContentId,
  contentSourcePageId,
  isFilterCustomizationLoading = false,
  savedPageFilterSelections,
  savedCustomTableFilterSelections,
  title,
  widgetFilterInstanceId,
  widgetId,
  isGroupedTable,
  ...useTableWidgetActionProps
}: TableContentProps) {
  const { t } = useTranslation();
  const { data: tenantInfo } = useTenantInfoData();

  const groupOnlyColumnId = groupOnlyVisualizationData?.value ?? null;
  // isGroupOnlyMode and isGroupedTable are mutually exclusive
  const isGroupOnlyMode = groupOnlyColumnId != null;

  const isGroupedPaginationEnabled = useMemo(
    () => tenantInfo?.enabledFeatures.includes('groupPagination') ?? false,
    [tenantInfo]
  );

  const { currentPage, currentPageSize, setCurrentPage, setCurrentPageSize } =
    usePersistentPagination(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      defaultPageSize
    );

  const { currentSortItems, setCurrentSortItems } = usePersistentSorting(
    pageFilterInstanceId,
    widgetFilterInstanceId,
    entity.config.sortColumns
  );

  const [currentSelectedRows, setCurrentSelectedRows] = useState<
    TableDataDto['items']
  >([]);
  const { clearTableRowSelection, setTableRowSelection } = useStore(
    useShallow((state) => ({
      clearTableRowSelection: state.clearTableRowSelection,
      setTableRowSelection: state.setTableRowSelection,
    }))
  );
  const dataSourceType = entity.config.cacheKey.dataSourceType;

  useEffect(
    () => () => {
      clearTableRowSelection(widgetId);
    },
    [clearTableRowSelection, widgetId]
  );

  const isDashboardTile = dashboardContentId != null;
  const isDashboardEditMode = useIsDashboardDraftMode(dashboardId);

  const { data: page } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: isFullScreenMode && !isDashboardTile,
  });

  const customPageFilterInstanceId =
    getCustomPageFilterInstanceId(pageFilterInstanceId);
  const tableFilterInstanceId = getTableFilterInstanceId(
    widgetFilterInstanceId,
    pageFilterInstanceId
  );

  const {
    currentSigningRequests,
    isSigningInProgress,
    storeSetTableGroupExpansion,
    storeDeleteTableGroupExpansions,
    storeWorkflowStateSelection,
    tableGroupExpansions,
  } = useStore(
    useShallow((state) => ({
      currentSigningRequests: state.currentSigningRequests,
      isSigningInProgress: state.isSigningInProgress(),
      storeSetTableGroupExpansion: state.setTableGroupExpansion,
      storeDeleteTableGroupExpansions: state.deleteTableGroupExpansions,
      storeWorkflowStateSelection:
        state.workflowStateSelections[pageFilterInstanceId]?.[
          widgetFilterInstanceId
        ],
      tableGroupExpansions:
        state.tableGroupExpansions[pageFilterInstanceId]?.[
          widgetFilterInstanceId
        ],
    }))
  );

  const workflowStateSelection = useMemo(
    () => storeWorkflowStateSelection?.map(({ states }) => states),
    [storeWorkflowStateSelection]
  );

  const globalFilterSelections = useEffectiveGlobalSelections();
  const pageFilterSelections = useSelections(pageFilterInstanceId);
  const combinedFilterSelections =
    useCombinedPageFilterSelections(pageFilterInstanceId);
  const tableFilterSelections = useSelections(tableFilterInstanceId);

  const activeCustomFilterConfigs = useMemo(
    () =>
      entity.config.columns
        .filter((c) => activeCustomFilterIds.includes(c.id))
        .map((c) => c.customFilter)
        .filter((c): c is NonNullable<typeof c> => c != null),
    [activeCustomFilterIds, entity.config.columns]
  );

  const globalFilterLoaded = useGlobalFilterLoaded();
  const customPageFilterLoaded = useStore(
    (state) => state.filterQueryLoaded[customPageFilterInstanceId] ?? false
  );
  const stakeAdjusted = useStakeAdjustmentStore((state) =>
    entity.config.enableStakeAdjustment
      ? state.stakeAdjusted[pageFilterInstanceId]
      : undefined
  );

  const pageFilterQuery = usePageFilterQuery(
    pageId,
    pageFilterInstanceId,
    undefined
  );
  const tableFilterQuery = useTableFilterQuery(
    widgetId,
    tableFilterInstanceId,
    pageFilterInstanceId,
    isDashboardTile ? undefined : entity.config.filters
  );
  const customTableFilterQuery = useCustomTableFilterQuery(
    widgetId,
    tableFilterInstanceId,
    pageFilterInstanceId,
    isDashboardTile ? [] : activeCustomFilterConfigs
  );
  const dashboardFilters = useDashboardTileFilters({
    enabled: isDashboardTile,
    editable: isDashboardEditMode,
    contentSourcePageId,
    tileInstanceId: dashboardContentId ?? widgetId,
    savedPageFilterSelections,
    savedCustomTableFilterSelections,
    pageFilterInstanceId,
    tableFilterInstanceId,
    widgetId,
    activeCustomFilterIds,
    customFilterColumns: entity.config.columns,
    isFilterCustomizationLoading,
  });

  const detailPageFilterSelections = useMemo(
    () => ({
      ...globalFilterSelections,
      ...pageFilterSelections,
    }),
    [globalFilterSelections, pageFilterSelections]
  );

  const userGroupSelection = useMemo(
    () =>
      customizedColumns
        .filter(
          (
            col
          ): col is TableColumnConfigDto &
            Required<Pick<TableColumnConfigDto, 'grouping'>> =>
            col.grouping != null
        )
        .map((col) => ({
          colId: col.id,
          level: col.grouping,
        })),
    [customizedColumns]
  );

  const requestPageFilter = isDashboardTile
    ? dashboardFilters.pageFilterSelections
    : combinedFilterSelections;
  const requestTableFilter = isDashboardTile
    ? dashboardFilters.customTableFilterSelections
    : tableFilterSelections;
  const requestWorkflowState = isDashboardTile
    ? undefined
    : workflowStateSelection;
  const requestStakeAdjusted = isDashboardTile ? undefined : stakeAdjusted;

  const widgetActions = useTableWidgetActions({
    activeCustomFilterIds,
    config: entity.config,
    currentSelectedRows,
    currentSortItems,
    pageFilter: isDashboardTile
      ? requestPageFilter
      : detailPageFilterSelections,
    pageFilterSelections: requestPageFilter,
    tableFilterSelections: requestTableFilter,
    workflowStateSelection: requestWorkflowState,
    onModalOpened,
    widgetId,
    title,
    customizedColumns,
    stakeAdjusted: requestStakeAdjusted,
    isGroupedTable,
    isGroupOnlyMode,
    pageData: page?.data,
    ...(isGroupOnlyMode ? { groupByColumnId: groupOnlyColumnId } : {}),
    ...useTableWidgetActionProps,
  });
  const pageEditActions = useContentEditActions(
    pageId,
    widgetId,
    widgetActions
  );
  const actions = useDashboardContentEditActions(
    dashboardId,
    dashboardContentId ?? widgetId,
    pageEditActions
  );

  const setTableGroupExpansion = useCallback(
    (groupId: string, value: TableGroupExpansionsValue) => {
      storeSetTableGroupExpansion(
        pageFilterInstanceId,
        widgetFilterInstanceId,
        groupId,
        value
      );
    },
    [pageFilterInstanceId, storeSetTableGroupExpansion, widgetFilterInstanceId]
  );

  const deleteTableGroupExpansions = useCallback(
    () =>
      storeDeleteTableGroupExpansions(
        pageFilterInstanceId,
        widgetFilterInstanceId
      ),
    [
      pageFilterInstanceId,
      storeDeleteTableGroupExpansions,
      widgetFilterInstanceId,
    ]
  );

  // page filter columns
  const pageFilterColumn = useMemo(
    () =>
      customizedColumns.find(
        (c) => c.type === 'pageFilter' && c.filterKey !== null
      ),
    [customizedColumns]
  );

  const {
    isChecked: isPageFilterColumnChecked,
    onChange: setFilterFromPageFilterColumn,
  } = usePageFilterColumnState({
    pageFilterColumn,
    pageFilterInstanceId,
    pageFilterSelections,
  });

  const { saveInlineCell, isInlineCellPending } = useInlineCellEditing({
    contentId: widgetId,
    cacheKey: entity.config.cacheKey,
    pageId,
    pageFilterSelections: requestPageFilter,
  });

  // @todo: persist active query key in UI Store to be able to restore last "state" on mount
  const queryKey: FetchDatatableDataQueryKeyParams = {
    page: currentPage,
    pageSize: currentPageSize,
    pageFilter: requestPageFilter,
    tableFilter: requestTableFilter,
    workflowStateSelection: requestWorkflowState,
    sortItems: currentSortItems,
    userGroupSelection,
    stakeAdjusted: requestStakeAdjusted,
  };

  const groupedQueryKey: FetchGroupedTableDataQueryKeyParams = {
    page: currentPage,
    pageSize: currentPageSize,
    pageFilter: requestPageFilter,
    tableFilter: requestTableFilter,
    workflowStateSelection: requestWorkflowState,
    sortItems: currentSortItems,
    groupByColumnId: groupOnlyColumnId ?? '',
    stakeAdjusted: requestStakeAdjusted,
  };

  const workflowStateReady =
    isDashboardTile ||
    !entity.config.workflowStateSelectionConfig?.options.length ||
    storeWorkflowStateSelection != null;

  const stakeAdjustmentReady =
    isDashboardTile ||
    !entity.config.enableStakeAdjustment ||
    stakeAdjusted !== undefined;

  const shouldRequestTableData = isDashboardTile
    ? entity.config.columns.length > 0 &&
      globalFilterLoaded &&
      dashboardFilters.ready
    : entity.config.columns.length > 0 &&
      globalFilterLoaded &&
      pageFilterQuery.isSuccess &&
      customPageFilterLoaded &&
      tableFilterQuery.isSuccess &&
      customTableFilterQuery.isSuccess &&
      workflowStateReady &&
      stakeAdjustmentReady;

  const regularQuery = useTableDataQuery(
    pageId,
    widgetId,
    entity.config.cacheKey,
    queryKey,
    shouldRequestTableData && !isGroupOnlyMode
  );

  const groupedQuery = useGroupedTableDataQuery(
    pageId,
    widgetId,
    entity.config.cacheKey,
    groupedQueryKey,
    shouldRequestTableData && isGroupOnlyMode
  );

  const { data, isFetching, error, refetch } = isGroupOnlyMode
    ? groupedQuery
    : regularQuery;

  const rowData = useMemo(() => {
    return data?.data.items.map((item, idx) => ({
      ...item,
      originalRowIndex: idx,
    }));
  }, [data?.data.items]);

  const transientRows = useMemo(
    () => getRowIdsForTransientWorkflows(rowData),
    [rowData]
  );

  useTransientWorkflowStatePolling(
    widgetId,
    transientRows,
    TRANSIENT_WORKFLOW_STATE_POLLING_INTERVAL,
    () => {
      refetch();
    }
  );

  const dataConfigs = data?.data.configs;
  const totalRows =
    data?.data.meta.groups?.totalCount ?? data?.data.meta.totalCount ?? 0;

  const firstVisibleCustomizedColumn = customizedColumns.find(
    (col) => !col.hidden
  );

  const footerTitleColumnKey = isGroupedTable
    ? GROUP_AUTO_COLUMN_ID
    : firstVisibleCustomizedColumn?.key;

  const { data: footer, isLoading: isFooterLoading } = useFooter({
    widgetId,
    tableConfig: entity.config,
    pageFilter: requestPageFilter,
    tableFilter: requestTableFilter,
    titleColumnKey: footerTitleColumnKey,
    shouldRequestFooterData: shouldRequestTableData,
    userGroupSelection: userGroupSelection ?? [],
    isGroupedTable,
    stakeAdjusted: requestStakeAdjusted,
    groupByColumnId: groupOnlyColumnId ?? undefined,
  });

  const sidebarScope = isFullScreenMode ? 'fullscreen' : 'page';

  const closePageSidebar = useStore((state) => state.closePageSidebar);
  const updatePageSidebarTableRow = useStore(
    (state) => state.updatePageSidebarTableRow
  );

  const { rowIdToHighlight: storedRowIdToHighlight, toggle: toggleSidebar } =
    useTableSidebarProperties(widgetId, entity.config, {
      scope: sidebarScope,
    });

  const activeSidebarContent = useStore((state) => {
    const session =
      sidebarScope === 'fullscreen'
        ? state.fullscreenPageSidebar
        : state.pageSidebar;

    if (
      !session.opened ||
      !session.active ||
      session.active.widgetId !== widgetId
    ) {
      return null;
    }

    return session.active;
  });

  const rowIdToHighlight = useMemo(() => {
    if (!activeSidebarContent || !rowData) {
      return storedRowIdToHighlight;
    }

    return (
      resolveActiveSidebarRowHighlightId(activeSidebarContent.data, rowData) ??
      storedRowIdToHighlight
    );
  }, [activeSidebarContent, rowData, storedRowIdToHighlight]);

  useEffect(() => {
    if (!activeSidebarContent || !rowData) {
      return;
    }

    const activeSidebarRow = activeSidebarContent.data;

    if (!isActiveSidebarRowInTableItems(activeSidebarRow, rowData)) {
      closePageSidebar(sidebarScope);
      return;
    }

    const resolvedRowId = resolveActiveSidebarRowHighlightId(
      activeSidebarRow,
      rowData
    );

    if (resolvedRowId == null) {
      return;
    }

    const rowIndex = Number.parseInt(resolvedRowId, 10);
    const freshRowData = rowData[rowIndex];

    if (freshRowData == null) {
      return;
    }

    if (
      activeSidebarRow.rowId === resolvedRowId &&
      activeSidebarRow.data === freshRowData
    ) {
      return;
    }

    updatePageSidebarTableRow(sidebarScope, {
      ...activeSidebarContent,
      data: {
        rowId: resolvedRowId,
        data: freshRowData,
      },
    });
  }, [
    activeSidebarContent,
    closePageSidebar,
    rowData,
    sidebarScope,
    updatePageSidebarTableRow,
  ]);

  const investmentLimitLinkColumnKey = useMemo(
    () =>
      entity.config.createRowConfig?.type === 'createInvestmentLimitRow'
        ? firstInvestmentLimitTextColumnKey(
            entity.config.columns,
            customizedColumns
          )
        : null,
    [
      customizedColumns,
      entity.config.columns,
      entity.config.createRowConfig?.type,
    ]
  );

  const columnDefs = useMemo(() => {
    return customizedColumns.map((col) =>
      configToColDef(col, widgetId, isFooterLoading, customizedColumns, {
        currentSigningRequests,
        dataConfigs,
        pageData: page?.data,
        footerData: footer,
        footerTitleColumnKey: footerTitleColumnKey,
        investmentLimitLinkColumnKey,
        isFullScreenMode,
        isGroupOnlyMode,
        isGroupedTable,
        isPageFilterColumnChecked,
        isSigningInProgress,
        saveInlineCell,
        isInlineCellPending,
        isSomeDataRowVisible: isDataRowVisible(
          userGroupSelection,
          tableGroupExpansions ?? {}
        ),
        onInvestmentLimitRowOpened,
        // Match widgetActions.pageFilter: dashboard tiles use the curated
        // request selection; normal pages keep global+page (no custom page
        // filters) for detail-page navigation.
        pageFilter: isDashboardTile
          ? requestPageFilter
          : detailPageFilterSelections,
        rowIdToHighlight,
        setFilterFromPageFilterColumn,
        showFullTextSearchResults: entity.config.showFullTextSearchResults,
        readonlyWorkflowActionHandler: onReadonlyWorkflowModalOpened,
        workflowActionHandler: onModalOpened,
        userGroupSelection: userGroupSelection ?? [],
      })
    );
  }, [
    customizedColumns,
    tableGroupExpansions,
    isGroupedTable,
    isGroupOnlyMode,
    entity.config.showFullTextSearchResults,
    page?.data,
    widgetId,
    isFooterLoading,
    currentSigningRequests,
    dataConfigs,
    footer,
    footerTitleColumnKey,
    investmentLimitLinkColumnKey,
    isDashboardTile,
    isFullScreenMode,
    isPageFilterColumnChecked,
    isSigningInProgress,
    saveInlineCell,
    isInlineCellPending,
    detailPageFilterSelections,
    requestPageFilter,
    rowIdToHighlight,
    setFilterFromPageFilterColumn,
    onInvestmentLimitRowOpened,
    onReadonlyWorkflowModalOpened,
    onModalOpened,
    userGroupSelection,
  ]);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<TableDataDto['items'][number]>) => {
      const rows = event.api.getSelectedRows();
      setCurrentSelectedRows(rows);
      // The store keeps a single selection, owned by the table the user last
      // selected rows in, so features outside the table (currently the AI
      // assistant) can pick it up.
      setTableRowSelection(widgetId, dataSourceType, rows);
    },
    [dataSourceType, setTableRowSelection, widgetId]
  );

  const onRowClickedCb = useCallback(
    (event: RowClickedEvent<TableDataDto['items'][number]>) => {
      if (event.node.id != null) {
        toggleSidebar?.({
          rowId: event.node.id,
          data: event.data ?? {},
        });
      }
    },
    [toggleSidebar]
  );

  const onColumnResized = useCallback(
    (event: ColumnResizedEvent<TableDataDto['items'][number]>) => {
      if (
        onColumnWidthChanged &&
        event.source === 'uiColumnResized' &&
        // last resize event in a row
        event.finished
      ) {
        const { column } = event;

        // Auto group column has first grouping level set as "field" property, therefore we need the colId to uniquely identify the column
        const columnId =
          column?.getColId() === AG_GRID_GROUP_AUTO_COLUMN_ID
            ? AG_GRID_GROUP_AUTO_COLUMN_ID
            : (column?.getColDef().field ?? column?.getColId());

        const width = column?.getActualWidth();
        if (columnId && width) {
          onColumnWidthChanged({ [columnId]: { width } });
        }
      }
    },
    [onColumnWidthChanged]
  );

  const onSortingChanged = (sortItem: SortColumnDto) => {
    setCurrentSortItems([sortItem, ...currentSortItems.slice(1)]);
  };

  const displayedError: string | undefined = useMemo(() => {
    if (error != null) {
      return extractErrorMessage(error);
    }
  }, [error]);

  const hidePagination = useMemo(() => {
    return (
      entity.config.hidePagination === true ||
      (entity.config.hidePagination === 'opportunistic' &&
        totalRows <= Math.min(...PAGINATION_SIZES))
    );
  }, [entity.config.hidePagination, totalRows]);

  const pinnedBottomRowData: FooterDataDto['mainFooter'][] = useMemo(() => {
    const pinnedRows = footer?.mainFooter != null ? [footer.mainFooter] : [];
    // more entries indicator row when pagination is hidden
    if (
      hidePagination &&
      footerTitleColumnKey &&
      totalRows - currentPageSize > 0
    ) {
      pinnedRows.push({
        [footerTitleColumnKey]: t('app.table.showMoreEntry', '', {
          count: totalRows - currentPageSize,
        }),
      });
    }
    return pinnedRows;
  }, [
    footer?.mainFooter,
    hidePagination,
    footerTitleColumnKey,
    totalRows,
    currentPageSize,
    t,
  ]);

  const showTableLoadingOverlay: boolean = useMemo(() => {
    return displayedError == null && (isFetching || !shouldRequestTableData);
  }, [displayedError, isFetching, shouldRequestTableData]);

  const groupColumnHeaderName = useGroupColumnHeaderName(customizedColumns);

  const paginationSizes = useMemo(() => {
    if (isGroupedTable) {
      if (isGroupedPaginationEnabled) {
        return PAGINATION_SIZES.slice(0, 2);
      }

      return [...PAGINATION_SIZES, DEFAULT_GROUP_TABLE_PAGE_SIZE];
    }

    return page?.data.type === 'detailPage'
      ? PAGINATION_SIZES_DETAIL_PAGE
      : PAGINATION_SIZES;
  }, [isGroupedTable, isGroupedPaginationEnabled, page?.data.type]);

  const tableContent = (
    <>
      {isDashboardTile ? (
        dashboardFilters.filterBar != null && (
          <WidgetFilterContainer>
            {dashboardFilters.filterBar}
          </WidgetFilterContainer>
        )
      ) : (
        <WidgetFilterContainer
          withBottomBorder={Boolean(
            entity.config.workflowStateSelectionConfig?.options.length
          )}
        >
          {isFullScreenMode && page != null ? (
            <PageFilters
              filterKey={pageFilterInstanceId}
              customPageFilterStoreId={customPageFilterStoreId}
              pageId={pageId}
              config={page.data.pageFilter}
              embedded={true}
            />
          ) : null}

          {entity.config.filters != null && (
            <TableFilters
              config={entity.config}
              pageFilterInstanceId={pageFilterInstanceId}
              tableFilterInstanceId={tableFilterInstanceId}
              widgetId={widgetId}
            />
          )}
          {activeCustomFilterIds.length > 0 && (
            <CustomTableFilters
              activeCustomFilterIds={activeCustomFilterIds}
              columnConfigs={entity.config.columns}
              pageFilterInstanceId={pageFilterInstanceId}
              tableFilterInstanceId={tableFilterInstanceId}
              widgetId={widgetId}
            />
          )}
          {isFullScreenMode &&
            entity.config.enableStakeAdjustment &&
            page != null && (
              <StakeAdjustmentToggle
                filterKey={pageFilterInstanceId}
                defaultEnabled={
                  page.data.enableStakeAdjustmentByDefault ?? false
                }
              />
            )}
        </WidgetFilterContainer>
      )}
      {
        /* add workflow state filters if present */
        !isDashboardTile &&
          entity.config.workflowStateSelectionConfig?.options.length && (
            <WorkflowStateFilters
              config={entity.config}
              pageFilterInstanceId={pageFilterInstanceId}
              widgetFilterInstanceId={widgetFilterInstanceId}
            />
          )
      }

      {columnDefs && (
        <UiTable
          columnDefs={columnDefs}
          enableRowSelection={entity.config.enableRowSelection}
          error={displayedError}
          // @tempfix: Find a better estimate for grouped table height
          expectedRowCount={isGroupedTable ? 12 : currentPageSize}
          gridOptions={{
            headerHeight: pixelSizeHelperPortalUi(56),
            rowHeight: pixelSizeHelperPortalUi(48),
          }}
          groupColumnField={
            columnDefs.find((col) => col.rowGroupIndex === 0)?.field
          }
          groupColumnHeaderName={groupColumnHeaderName}
          groupColumnWidth={groupColumnWidth}
          isGroupOpenByDefault={(params) => {
            if (isFullScreenMode) {
              // expand all groups by default in fullscreen mode
              return true;
            }
            const state =
              params.rowNode.id != null
                ? tableGroupExpansions?.[params.rowNode.id]?.expanded
                : undefined;
            const groupDefaultExpanded = entity.config.groupDefaultExpanded;
            return (
              state ??
              (groupDefaultExpanded != null
                ? params.level < groupDefaultExpanded
                : false)
            );
          }}
          loading={showTableLoadingOverlay}
          onColumnResized={onColumnResized}
          onRowGroupOpened={(event) => {
            const { id, rowGroupIndex } = event.node;
            if (id != null && rowGroupIndex != null) {
              setTableGroupExpansion(id, {
                expanded: event.expanded,
                groupLevel: rowGroupIndex,
              });
            }
          }}
          onRowDataUpdated={(event) => {
            // Reset information about expanded groups and recreate it from the current state.
            // Groups can be expanded by default via PoCo configuration and this does not trigger an onRowGroupOpened event.
            deleteTableGroupExpansions();

            for (let i = 0; i < event.api.getDisplayedRowCount(); i++) {
              const n = event.api.getDisplayedRowAtIndex(i);
              if (
                n != null &&
                n.group &&
                n.expanded &&
                n.id != null &&
                n.rowGroupIndex != null
              ) {
                setTableGroupExpansion(n.id, {
                  expanded: n.expanded,
                  groupLevel: n.rowGroupIndex,
                });
              }
            }
          }}
          onSelectionChanged={onSelectionChanged}
          onSortingChanged={onSortingChanged}
          onRowClickedCb={toggleSidebar ? onRowClickedCb : undefined}
          pinnedBottomRowData={pinnedBottomRowData}
          rowData={rowData}
          // let user only sort by one (primary) column
          // technically data could be sorted by up to three different columns
          sortItems={currentSortItems.slice(0, 1)}
        />
      )}
    </>
  );

  const tableWidget = hidePagination ? (
    <Widget
      actions={actions}
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      title={title}
      chipGroup={groupOnlyVisualizationData}
    >
      {tableContent}
    </Widget>
  ) : (
    <WidgetWithPaginator
      actions={actions}
      activePage={currentPage}
      activePaginationSize={currentPageSize}
      availablePaginationSizes={paginationSizes}
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      onPageChange={setCurrentPage}
      onPaginationSizeChange={setCurrentPageSize}
      title={title}
      totalEntries={totalRows}
      selectedRowsCount={currentSelectedRows.length}
      isDocViewWidget={entity.config.cacheKey.dataSourceType === 'docview'}
      chipGroup={groupOnlyVisualizationData}
      paginatorDisabled={showTableLoadingOverlay}
    >
      {tableContent}
    </WidgetWithPaginator>
  );

  return tableWidget;
}
